import { Moon, Sunrise, Sunset, Camera, Clock } from "lucide-react";
import { formatTime } from "../lib/format";
import { dictionaries } from "../lib/i18n";
import type { DayPlan, Stop, UiLang } from "../lib/types";
import { stopSunlight } from "../utils/sunlight";

/**
 * Sol og dagslys på det tidspunktet du faktisk er der.
 *
 * Dagslyset sto allerede i planen, men som en egenskap ved *dagen*: seks timer
 * og førti minutter, regnet for dagens første stopp. Det er riktig og lite til
 * hjelp. Spørsmålet man har foran dag 4 i oktober er om det er lyst når man
 * kommer til Stegastein, og det avhenger av at Stegastein er dagens siste
 * stopp etter fem timer kjøring.
 *
 * Ankomsttiden er et anslag — se utils/sunlight.ts — og står som «rundt», ikke
 * som et klokkeslett. Det er ærligere, og det er også presist nok: forskjellen
 * mellom halv fem og fem betyr ingenting, forskjellen mellom halv fem og halv
 * ni betyr alt.
 */

interface Props {
  stop: Stop;
  day: DayPlan;
  lang: UiLang;
}

export function SunlightCard({ stop, day, lang }: Props) {
  const t = dictionaries[lang];
  const sun = stopSunlight(stop, day);

  // Uten dato er soloppgang et spørsmål uten svar. Kalleren skjuler seksjonen.
  if (!sun) return null;

  const midnightSun = day.daylight?.midnightSun;
  const polarNight = day.daylight?.polarNight;

  return (
    <div className={`sunlight ${sun.darknessWarning ? "sunlight--dark" : ""}`}>
      <div className="sunlight-arrival">
        <Clock size={13} />
        <b>{t.sunArrival(formatTime(sun.arrival, lang))}</b>
        <span className="sunlight-verdict">
          {sun.isDaylight ? t.sunDaylight : t.sunAfterDark}
        </span>
      </div>

      {/*
        Nord for polarsirkelen er soloppgang og solnedgang ikke tall som
        mangler — de finnes ikke. Da er det den opplysningen som skal stå,
        ikke to tankestreker der klokkeslettene skulle vært.
      */}
      {midnightSun || polarNight ? (
        <div className="sunlight-extreme">
          <Moon size={13} />
          {midnightSun ? t.sunMidnight : t.sunPolarNight}
        </div>
      ) : (
        <div className="sunlight-times">
          <span className="sunlight-time">
            <Sunrise size={13} />
            <small>{t.sunRise}</small>
            <b>{formatTime(sun.sunrise, lang)}</b>
          </span>
          <span className="sunlight-time">
            <Sunset size={13} />
            <small>{t.sunSet}</small>
            <b>{formatTime(sun.sunset, lang)}</b>
          </span>
          {sun.goldenHour && (
            <span className="sunlight-time">
              <Camera size={13} />
              <small>{t.sunGolden}</small>
              <b>{formatTime(sun.goldenHour, lang)}</b>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
